import { toast } from '@heidi/design-system/toast';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type { ApprovalVerdict } from '../../../../contracts/harness.types';
import { sharedAgentMessages } from '../../../../shared.messages';

/** Resolver the projects transport hands down to every inline approval surface. */
export type ResolveInlineApproval = (
  approvalId: string,
  verdict: ApprovalVerdict,
  reason?: string,
  modifiedInput?: Record<string, unknown> | null
) => Promise<void>;

interface UseInlineApprovalResolveArgs {
  approvalId: string;
  onResolve: ResolveInlineApproval;
}

export interface InlineApprovalResolve {
  /** Latched verdict while the resolve is in flight; null once settled. */
  pendingVerdict: ApprovalVerdict | null;
  isBusy: boolean;
  submit: (verdict: ApprovalVerdict) => void;
}

/**
 * Shared submit path for the inline approval surfaces. Latches the verdict so
 * the buttons can't be double-fired, and on failure clears the latch (so the
 * user can retry) and surfaces the error as a destructive toast.
 *
 * Neither surface supports inline editing: approving omits `modified_input` so
 * the backend uses the original tool input, denying carries an explicit `null`
 * per the wire contract.
 */
export const useInlineApprovalResolve = ({
  approvalId,
  onResolve,
}: UseInlineApprovalResolveArgs): InlineApprovalResolve => {
  const intl = useIntl();
  const [pendingVerdict, setPendingVerdict] = useState<ApprovalVerdict | null>(null);
  const isBusy = pendingVerdict !== null;

  const submit = useCallback(
    (verdict: ApprovalVerdict) => {
      if (isBusy) return;
      setPendingVerdict(verdict);

      const modifiedInput = verdict === 'approve' ? undefined : null;

      void onResolve(approvalId, verdict, undefined, modifiedInput).catch(
        (err: unknown) => {
          setPendingVerdict(null);
          const message =
            err instanceof Error
              ? err.message
              : intl.formatMessage({
                  defaultMessage: 'Unknown error',
                  id: '7POY7QQrzT',
                  description: 'Toast fallback when an approval fails without details',
                });
          toast({
            title: intl.formatMessage(sharedAgentMessages.couldntResolve),
            description: message,
            tone: 'destructive',
          });
        }
      );
    },
    [approvalId, intl, isBusy, onResolve]
  );

  return { pendingVerdict, isBusy, submit };
};
